import api from "./api";

export type LimpiezaFrecuenciaKey =
  | "quincenal"
  | "mensual"
  | "bimestral"
  | "trimestral"
  | "cuatrimestral"
  | "semestral"
  | "anual"
  | "otros";

export interface LimpiezaPlanificacion {
  id: number;
  frecuencia: string;
  frecuencia_normalizada: LimpiezaFrecuenciaKey;
  frecuencia_label: string;
  estado: string;
  codigo_estado: string;
  cant_horas: string;
  tipo_lavado: string;
  origen: string;
  fecha: string;
  fecha_iso?: string;
  dia?: number;
  mes?: number;
  anio?: number;
}

export interface LimpiezaContacto {
  nombre?: string;
  correo?: string;
  telefono?: string;
}

export interface LimpiezaPsInfo {
  id?: number;
  nombre: string;
  codigo: string;
  direccion?: string;
  unidad_negocio?: string;
  contacto?: LimpiezaContacto;
  ejecutivo?: LimpiezaContacto;
  fiscal?: LimpiezaContacto;
  jefe?: LimpiezaContacto;
  horas_especiales_mes?: number;
}

export interface LimpiezaCalendario {
  mes: number;
  anio: number;
  etiqueta: string;
  inicio: string;
  fin: string;
}

export interface LimpiezaResumen {
  total: number;
  por_frecuencia: Record<string, number>;
  por_estado: Record<string, number>;
  horas_especiales_mes?: number;
}

export interface LimpiezaFrecuenciaItem {
  key: LimpiezaFrecuenciaKey;
  label: string;
}

export interface LimpiezaResumenMensual {
  calendario: LimpiezaCalendario;
  frecuencias: LimpiezaFrecuenciaItem[];
  resumen: {
    total: number;
    por_frecuencia: Record<LimpiezaFrecuenciaKey, number>;
  };
  puntos_servicio: Array<{
    ps_id?: number;
    ps_codigo: string;
    ps_nombre: string;
    total: number;
    por_frecuencia: Record<LimpiezaFrecuenciaKey, number>;
  }>;
}

export interface LimpiezaPlanificacionesPayload {
  ps: LimpiezaPsInfo;
  calendario: LimpiezaCalendario;
  planificaciones: LimpiezaPlanificacion[];
  frecuencias: Record<LimpiezaFrecuenciaKey, LimpiezaPlanificacion[]>;
  resumen: LimpiezaResumen;
}

export interface LimpiezaPlanificacionDetalle extends LimpiezaPlanificacion {
  tipo_lavado_id?: number;
  tipo_hora?: string;
  tipo_hora_id?: number;
  perfil_requerido?: string;
  especialista_id?: number;
  sector?: string;
  area?: string;
  cantidad?: number;
  motivo?: string;
  orden_trabajo?: string;
  ps_id?: number;
  ps_codigo?: string;
  ps_nombre?: string;
  total_asignado?: string;
  editable?: boolean;
  cupo_planificado?: string;
  cupo_planificado_min?: number;
  cupo_utilizada?: string;
  fecha_label?: string;
}

export interface LimpiezaAsignacion {
  id: number;
  actividad_cab_id?: number;
  operario_id: number;
  operario_legajo: string;
  operario_nombre: string;
  horas: string;
  fecha_ejecucion?: string;
  hora_inicio?: string;
  hora_fin?: string;
  sector?: string;
  area?: string;
  cantidad?: number;
}

export interface OperarioDisponible {
  id: number;
  legajo: string;
  nombre: string;
  total_asignado?: number;
}

export interface LimpiezaDetalleResponse {
  planificacion: LimpiezaPlanificacionDetalle;
  asignaciones: LimpiezaAsignacion[];
}

export interface LimpiezaPlanificacionesParams {
  psId: number;
  psCodigo: string;
  psNombre: string;
  mes: number;
  anio: number;
}

export async function fetchPlanificaciones(
  params: LimpiezaPlanificacionesParams
): Promise<LimpiezaPlanificacionesPayload> {
  const response = await api.get("limpiezas-profundas/planificaciones", {
    params: {
      ps_id: params.psId,
      ps_codigo: params.psCodigo,
      ps_nombre: params.psNombre,
      mes: params.mes,
      anio: params.anio,
    },
  });

  return response.data?.datos as LimpiezaPlanificacionesPayload;
}

export async function fetchPlanificacionDetalle(lpId: number): Promise<LimpiezaDetalleResponse> {
  const response = await api.get(`limpiezas-profundas/planificaciones/${lpId}`);
  return response.data?.datos as LimpiezaDetalleResponse;
}

export async function fetchResumenMensual(params?: {
  mes?: number;
  anio?: number;
}): Promise<LimpiezaResumenMensual> {
  const response = await api.get("limpiezas-profundas/resumen-mensual", {
    params: {
      mes: params?.mes,
      anio: params?.anio,
    },
  });
  return response.data?.datos as LimpiezaResumenMensual;
}

export async function buscarOperarios(psId: number, term?: string): Promise<OperarioDisponible[]> {
  const params = new URLSearchParams({ ps_id: String(psId) });
  if (term) params.append("q", term);
  const response = await api.get(`limpiezas-profundas/operarios-disponibles?${params.toString()}`);
  return response.data?.datos ?? [];
}

export interface AsignacionPayload {
  operario_id: number;
  fecha_ejecucion: string;
  hora_inicio: string;
  hora_fin: string;
  sector?: string;
  area?: string;
  cantidad?: number;
}

interface AsignacionesResponse {
  asignaciones: LimpiezaAsignacion[];
  total_asignado: string;
}

export async function crearAsignacion(lpId: number, payload: AsignacionPayload): Promise<AsignacionesResponse> {
  const { data } = await api.post(`limpiezas-profundas/planificaciones/${lpId}/operarios`, payload);
  return data?.datos as AsignacionesResponse;
}

export async function actualizarAsignacion(
  lpId: number,
  asignacionId: number,
  payload: AsignacionPayload
): Promise<AsignacionesResponse> {
  const { data } = await api.put(`limpiezas-profundas/planificaciones/${lpId}/operarios/${asignacionId}`, payload);
  return data?.datos as AsignacionesResponse;
}

export async function eliminarAsignacion(
  lpId: number,
  asignacionId: number
): Promise<AsignacionesResponse> {
  const { data } = await api.delete(`limpiezas-profundas/planificaciones/${lpId}/operarios/${asignacionId}`);
  return data?.datos as AsignacionesResponse;
}

export async function finalizarPlanificacion(lpId: number, payload: { orden_trabajo: string; informe?: string }) {
  return api.post(`limpiezas-profundas/planificaciones/${lpId}/finalizar`, payload);
}

export default {
  obtenerPlanificaciones: fetchPlanificaciones,
  fetchPlanificacionDetalle,
  detalle: fetchPlanificacionDetalle,
  fetchResumenMensual,
  buscarOperarios,
  crearAsignacion,
  actualizarAsignacion,
  eliminarAsignacion,
  finalizarPlanificacion,
};
